import React, { Component } from 'react';
import PropTypes from 'prop-types';

import {
  BATemplate,
  BATemplateSceneGraph,
} from '@companycam/companycam-bacore';

import {
  WGLLog,
  WGLContext,
  WGLFBO,
  WGLTexture,
  WGLShader,
  WGLQuad,
} from '../wgl';
import * as GLSL from '../glsl';

import { addFancyTouchListener } from './touches';

const MAX_OUTPUT_DIM = 1440;

const COLOR1_TEXTURE_UNIT = 0;
const COLOR2_TEXTURE_UNIT = 1;
const COLOR3_TEXTURE_UNIT = 2;

const PAN_MODE_SURFACE = 'PAN_MODE_SURFACE';
const PAN_MODE_BEFORE = 'PAN_MODE_BEFORE';
const PAN_MODE_AFTER = 'PAN_MODE_AFTER';

class WGLRenderer extends Component {
  //-----------------------------------------
  static _loadImage(url, onDone) {
    const img = new Image();
    img.crossOrigin = '';
    img.onload = () => onDone(img);

    // NOTE: The query parameter prevents an occasional CORS error due to caching.
    // cf. https://stackoverflow.com/questions/48350432/cors-on-canvas-not-working-on-chrome-and-safari
    img.src = `${url}?t=${Date.now()}`;
  }
  //-----------------------------------------

  constructor(props) {
    super(props);

    this.gl = null;
    this.wglObjects = {};

    this.aspectRatios = {
      aspectRatioSurface: 1,
      aspectRatioA: 1,
      aspectRatioB: 1,
      aspectRatioL: 1,
    };

    this.template = new BATemplate(props.templatePOJO);
    this.templateSceneGraph = new BATemplateSceneGraph(props.templatePOJO);
    this.updateAspectRatios();
  }

  componentDidMount() {
    const canvas = this._componentCanvas;
    if (canvas) {
      this.glInit();
      this.updateCanvasDims();
    }

    window.addEventListener('resize', () => {
      this.updateCanvasDims();
    });
  }

  componentDidUpdate(prevProps) {
    // resize GL textures only when needed
    if (this.props.imageMask !== prevProps.imageMask) {
      this.glUpdateImageMask(this.props.imageMask);
    }
    if (this.props.imageOverlay !== prevProps.imageOverlay) {
      this.glUpdateImageOverlay(this.props.imageOverlay);
    }
    if (this.props.imageBefore !== prevProps.imageBefore) {
      this.glUpdateImageBefore(this.props.imageBefore);
    }
    if (this.props.imageAfter !== prevProps.imageAfter) {
      this.glUpdateImageAfter(this.props.imageAfter);
    }
    if (this.props.imageLogo !== prevProps.imageLogo) {
      this.glUpdateImageLogo(this.props.imageLogo);
    }

    // update scene graph and re-render if template changes
    if (this.props.templatePOJO !== prevProps.templatePOJO) {
      this.template = new BATemplate(this.props.templatePOJO);
      this.templateSceneGraph = new BATemplateSceneGraph(
        this.props.templatePOJO,
      );
      this.updateAspectRatios();
      this.glRender();
    }
  }

  componentWillUnmount() {
    const { gl } = this;
    if (!gl) {
      return;
    }

    const {
      fbo,
      quad,
      quadCopy,
      quadBAPass1,
      quadBAPass2,
      texBlend,
      texMask,
      texOverlay,
      texBefore,
      texAfter,
      texLogo,
    } = this.wglObjects;

    fbo.free(gl);
    quad.free(gl);

    quadCopy.free(gl);
    quadBAPass1.free(gl);
    quadBAPass2.free(gl);

    texBlend.free(gl);
    texMask.free(gl);
    texOverlay.free(gl);
    texBefore.free(gl);
    texAfter.free(gl);
    texLogo.free(gl);
  }

  getCurrentTemplateAspect() {
    return this.template ? this.template.getAspect() : 1;
  }

  getOutputSize() {
    const aspect = this.getCurrentTemplateAspect();
    if (!aspect) {
      return [MAX_OUTPUT_DIM, MAX_OUTPUT_DIM];
    }

    return aspect < 1
      ? [MAX_OUTPUT_DIM * aspect, MAX_OUTPUT_DIM]
      : [MAX_OUTPUT_DIM, MAX_OUTPUT_DIM / aspect];
  }

  requestSave(onSave) {
    const canvas = this._componentCanvas;
    if (!canvas) {
      return;
    }

    // Reset surface transformation and render at output size
    this.templateSceneGraph.resetSurface();
    const outputDims = this.getOutputSize();
    this._setCanvasDims(outputDims, false);
    this.glRender();

    // Save rendered image, then put canvas size back to normal
    const dataURL = canvas.toDataURL('image/jpeg', 0.9);
    onSave(dataURL);
    this.updateCanvasDims();

    // canvas.toBlob((blob) => {
    //   onSave(blob);
    //   this.updateCanvasDims();
    // }, 'image/jpeg', 0.9);
  }

  resetSurface() {
    this.templateSceneGraph.resetSurface();
    this.glRender();
  }

  resetA() {
    this.templateSceneGraph.resetA();
    this.glRender();
  }

  resetB() {
    this.templateSceneGraph.resetB();
    this.glRender();
  }

  rotateA() {
    this.templateSceneGraph.rotateA();
    this.glRender();
  }

  rotateB() {
    this.templateSceneGraph.rotateB();
    this.glRender();
  }

  updateAspectRatios() {
    this.templateSceneGraph.setAspectRatios(this.aspectRatios);
  }

  _setCanvasDims(dims, usePixelRatio) {
    const canvas = this._componentCanvas;
    if (!canvas) {
      return;
    }

    const pixelRatio = usePixelRatio ? window.devicePixelRatio : 1;

    canvas.width = dims[0] * pixelRatio;
    canvas.height = dims[1] * pixelRatio;
    canvas.style.width = `${dims[0]}px`;
    canvas.style.height = `${dims[1]}px`;

    this.aspectRatios.surfaceAspectRatio = canvas.width / canvas.height;
    this.updateAspectRatios();

    const { texBlend } = this.wglObjects;
    texBlend.resize(this.gl, canvas.width, canvas.height);
    this.glRender();
  }

  updateCanvasDims() {
    const canvasWrap = this._componentCanvasWrap;
    if (!canvasWrap) {
      return;
    }

    const canvasDims = [canvasWrap.clientWidth, canvasWrap.clientHeight];
    this._setCanvasDims(canvasDims, true);
  }

  glUpdateImageMask(url) {
    const { gl } = this;
    const { texMask } = this.wglObjects;

    WGLRenderer._loadImage(url, (img) => {
      texMask.resize(gl, img.width, img.height);
      texMask.setImage(gl, img);
      this.glRender();
    });
  }

  glUpdateImageOverlay(url) {
    const { gl } = this;
    const { texOverlay } = this.wglObjects;

    WGLRenderer._loadImage(url, (img) => {
      texOverlay.resize(gl, img.width, img.height);
      texOverlay.setImage(gl, img);
      this.glRender();
    });
  }

  glUpdateImageBefore(url) {
    const { gl } = this;
    const { texBefore } = this.wglObjects;

    WGLRenderer._loadImage(url, (img) => {
      this.aspectRatios.aspectRatioA = img.width / img.height;
      this.updateAspectRatios();

      texBefore.resize(gl, img.width, img.height);
      texBefore.setImage(gl, img);
      this.glRender();
    });
  }

  glUpdateImageAfter(url) {
    const { gl } = this;
    const { texAfter } = this.wglObjects;

    WGLRenderer._loadImage(url, (img) => {
      this.aspectRatios.aspectRatioB = img.width / img.height;
      this.updateAspectRatios();

      texAfter.resize(gl, img.width, img.height);
      texAfter.setImage(gl, img);
      this.glRender();
    });
  }

  glUpdateImageLogo(url) {
    const { gl } = this;
    const { texLogo } = this.wglObjects;

    WGLRenderer._loadImage(url, (img) => {
      this.aspectRatios.aspectRatioL = img.width / img.height;
      this.updateAspectRatios();

      texLogo.resize(gl, img.width, img.height);
      texLogo.setImage(gl, img);
      this.glRender();
    });
  }

  glInit() {
    const canvas = this._componentCanvas;
    if (!canvas) {
      return;
    }

    WGLLog.disable();

    const gl = WGLContext.get(canvas);
    this.gl = gl;

    const fbo = new WGLFBO(gl, 'test FBO');
    const quad = new WGLQuad(gl, 'test quad');

    // shaders
    // ----------------------------------
    const VANAME_POS = 'vertexPosition';
    const VANAME_TEX = 'vertexTex';

    const quadCopy = new WGLShader(gl, 'quad copy', {
      srcVert: GLSL.quadVert,
      srcFrag: GLSL.quadFragCopy,
      vaNames: [VANAME_POS, VANAME_TEX],
      uNames: ['texColor'],
    });

    const quadBAPass1 = new WGLShader(gl, 'BA pass 1 (composite)', {
      srcVert: GLSL.quadVert,
      srcFrag: GLSL.quadFragBAPass1,
      vaNames: [VANAME_POS, VANAME_TEX],
      uNames: [
        'imageA',
        'imageB',
        'imageMask',
        'swapChannels',
        'colorAdjustA',
        'colorAdjustB',
        'texTransformOut',
        'texTransformA',
        'texTransformB',
      ],
    });

    const quadBAPass2 = new WGLShader(gl, 'BA pass 2 (overlay)', {
      srcVert: GLSL.quadVert,
      srcFrag: GLSL.quadFragBAPass2,
      vaNames: [VANAME_POS, VANAME_TEX],
      uNames: ['imageL', 'imageOverlay', 'texTransformOut', 'texTransformL'],
    });
    // ----------------------------------

    // textures: draw target(s)
    const texBlend = new WGLTexture(
      gl,
      'blend target texture',
      canvas.width,
      canvas.height,
    );

    // textures: assets
    const texMask = new WGLTexture(gl, 'template mask', 1, 1);
    const texOverlay = new WGLTexture(gl, 'template overlay', 1, 1);
    const texBefore = new WGLTexture(gl, 'template test before', 1, 1);
    const texAfter = new WGLTexture(gl, 'template test after', 1, 1);
    const texLogo = new WGLTexture(gl, 'template test logo', 1, 1);

    // done!
    this.wglObjects = {
      fbo,
      quad,
      quadCopy,
      quadBAPass1,
      quadBAPass2,
      texBlend,
      texMask,
      texOverlay,
      texBefore,
      texAfter,
      texLogo,
    };

    this.glUpdateImageMask(this.props.imageMask);
    this.glUpdateImageOverlay(this.props.imageOverlay);
    this.glUpdateImageBefore(this.props.imageBefore);
    this.glUpdateImageAfter(this.props.imageAfter);
    this.glUpdateImageLogo(this.props.imageLogo);

    // add touch listener
    // ------------------------------------------
    let isPressed = false;
    let downPos = [0, 0];
    addFancyTouchListener(canvas, {
      startSingle: (pNewScr) => {
        isPressed = true;
        downPos = pNewScr;
      },
      moveSingle: (pNewScr) => {
        if (!isPressed) {
          return;
        }

        const pOld = [downPos[0] / canvas.width, downPos[1] / canvas.height];
        const pNew = [pNewScr[0] / canvas.width, pNewScr[1] / canvas.height];

        switch (this.props.panMode) {
          case PAN_MODE_BEFORE:
            this.templateSceneGraph.handleAPan(pOld, pNew);
            break;
          case PAN_MODE_AFTER:
            this.templateSceneGraph.handleBPan(pOld, pNew);
            break;
          case PAN_MODE_SURFACE:
          default:
            this.templateSceneGraph.handleSurfacePan(pOld, pNew);
        }
        this.glRender();

        downPos = pNewScr;
      },
      endSingle: () => {
        isPressed = false;
      },
      // TODO: Pinch to zoom surface
      startDouble: (pNewScr, qNewScr) => {},
      moveDouble: (pNewScr, qNewScr) => {},
      endDouble: () => {},
    });
    // ------------------------------------------
  }

  glRender() {
    const { gl } = this;
    const canvas = this._componentCanvas;
    if (!gl || !canvas) {
      return;
    }

    const {
      fbo,
      quad,
      quadCopy,
      quadBAPass1,
      quadBAPass2,
      texBlend,
      texMask,
      texOverlay,
      texBefore,
      texAfter,
      texLogo,
    } = this.wglObjects;

    const {
      matA,
      matB,
      matL,
      matOut,
    } = this.templateSceneGraph.getTexTransforms();

    fbo.bind(gl);
    texBlend.bindWrite(gl);

    gl.colorMask(true, true, true, true);
    gl.depthMask(false);

    gl.clearColor(1, 0, 0, 0);
    gl.clear(gl.DEPTH_BUFFER_BIT | gl.COLOR_BUFFER_BIT);

    // BA pass 1
    // --------------------------------
    quadBAPass1.use(gl);

    quadBAPass1.setU1i(gl, 'imageMask', COLOR1_TEXTURE_UNIT);
    quadBAPass1.setU1i(gl, 'imageA', COLOR2_TEXTURE_UNIT);
    quadBAPass1.setU1i(gl, 'imageB', COLOR3_TEXTURE_UNIT);

    // pull color adjust information
    const dataA = this.template.getColorAdjustA();
    const dataB = this.template.getColorAdjustB();

    const colorAdjustA = dataA.enableColorAdjust
      ? [dataA.brightness, dataA.contrast, dataA.saturation]
      : [0, 1, 1];
    const colorAdjustB = dataB.enableColorAdjust
      ? [dataB.brightness, dataB.contrast, dataB.saturation]
      : [0, 1, 1];

    quadBAPass1.setUVec3(gl, 'colorAdjustA', colorAdjustA);
    quadBAPass1.setUVec3(gl, 'colorAdjustB', colorAdjustB);
    quadBAPass1.setU1i(gl, 'swapChannels', !!this.template.getSwapChannels());

    quadBAPass1.setUMat3Raw(gl, 'texTransformOut', matOut);
    quadBAPass1.setUMat3Raw(gl, 'texTransformA', matA);
    quadBAPass1.setUMat3Raw(gl, 'texTransformB', matB);

    texMask.bind(gl, COLOR1_TEXTURE_UNIT);
    texBefore.bind(gl, COLOR2_TEXTURE_UNIT);
    texAfter.bind(gl, COLOR3_TEXTURE_UNIT);

    quadBAPass1.prepareVertexAttribs(gl, quad);
    quad.draw(gl);

    texMask.unbind(gl, COLOR1_TEXTURE_UNIT);
    texBefore.unbind(gl, COLOR2_TEXTURE_UNIT);
    texAfter.unbind(gl, COLOR3_TEXTURE_UNIT);

    quadBAPass1.unuse(gl);
    // --------------------------------

    // BA pass 2
    // --------------------------------
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    quadBAPass2.use(gl);

    quadBAPass2.setU1i(gl, 'imageOverlay', COLOR1_TEXTURE_UNIT);
    quadBAPass2.setU1i(gl, 'imageL', COLOR2_TEXTURE_UNIT);

    quadBAPass2.setUMat3Raw(gl, 'texTransformOut', matOut);
    quadBAPass2.setUMat3Raw(gl, 'texTransformL', matL);

    texOverlay.bind(gl, COLOR1_TEXTURE_UNIT);
    texLogo.bind(gl, COLOR2_TEXTURE_UNIT);

    quadBAPass2.prepareVertexAttribs(gl, quad);
    quad.draw(gl);

    texOverlay.unbind(gl, COLOR1_TEXTURE_UNIT);
    texLogo.unbind(gl, COLOR2_TEXTURE_UNIT);

    quadBAPass2.unuse(gl);

    gl.disable(gl.BLEND);
    // --------------------------------

    texBlend.unbindWrite(gl);
    fbo.unbind(gl);

    // draw result
    // ---------------------------
    gl.colorMask(true, true, true, true);
    gl.depthMask(false);

    gl.viewport(0, 0, canvas.width, canvas.height);

    quadCopy.use(gl);
    quadCopy.setU1i(gl, 'texColor', COLOR1_TEXTURE_UNIT);
    texBlend.bind(gl, COLOR1_TEXTURE_UNIT);
    quadCopy.prepareVertexAttribs(gl, quad);
    quad.draw(gl);
    texBlend.unbind(gl, COLOR1_TEXTURE_UNIT);
    quadCopy.unuse(gl);
    //---------------------------
  }

  render() {
    // TODO: should add nice error display if canvas or WebGL not supported
    return (
      <div
        ref={(c) => {
          this._componentCanvasWrap = c;
        }}
        className="canvas-wrap"
      >
        <canvas
          ref={(c) => {
            this._componentCanvas = c;
          }}
        >
          Canvas not supported!
        </canvas>
      </div>
    );
  }
}

WGLRenderer.propTypes = {
  templatePOJO: PropTypes.object.isRequired,

  imageMask: PropTypes.string.isRequired,
  imageOverlay: PropTypes.string.isRequired,

  imageBefore: PropTypes.string.isRequired,
  imageAfter: PropTypes.string.isRequired,
  imageLogo: PropTypes.string.isRequired,

  panMode: PropTypes.string.isRequired,
};

export { PAN_MODE_SURFACE, PAN_MODE_BEFORE, PAN_MODE_AFTER, WGLRenderer };
